import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleCheck,
  faClock,
  faTimesCircle,
  faExternalLink,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useContext, useMemo, useState } from 'react';

import { Color } from '@/constants/color';
import PersonContext from '@/pages/Person/PersonContext';
import { ReleaseOfInformationStatus } from '@/types/releaseOfInformation';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import ListCard from '@/ui/components/ListCard';
import Tooltip from '@/ui/components/Tooltip/Tooltip';
import formatPersonName from '@/utils/formatPersonName';
import formatStaffName from '@/utils/formatStaffName';

import { Props } from './types';

const statusColorMap: Record<ReleaseOfInformationStatus, Color> = {
  [ReleaseOfInformationStatus.ACTIVE]: Color.GREEN,
  [ReleaseOfInformationStatus.PENDING]: Color.YELLOW,
  [ReleaseOfInformationStatus.EXPIRED]: Color.GRAY,
  [ReleaseOfInformationStatus.REVOKED]: Color.RED
};

const statusIconMap: Record<ReleaseOfInformationStatus, IconProp> = {
  [ReleaseOfInformationStatus.ACTIVE]: faCircleCheck,
  [ReleaseOfInformationStatus.PENDING]: faClock,
  [ReleaseOfInformationStatus.EXPIRED]: faTimesCircle,
  [ReleaseOfInformationStatus.REVOKED]: faTimesCircle
};

const ReleaseOfInformationItem = ({ releaseOfInformation }: Props) => {
  const [showAllDetails, setShowAllDetails] = useState(false);
  const {
    person: { external_links }
  } = useContext(PersonContext);

  const healthieDomain = external_links.healthie
    ? new URL(external_links.healthie.url).origin
    : undefined;
  const { form_answer_group_healthie_id } = releaseOfInformation;
  const showHealthieLink = form_answer_group_healthie_id && healthieDomain;

  const status = useMemo(() => {
    if (releaseOfInformation.revoked_at) {
      return { key: ReleaseOfInformationStatus.REVOKED, label: 'Revoked' };
    }
    if (releaseOfInformation.expired_at && new Date(releaseOfInformation.expired_at) < new Date()) {
      return { key: ReleaseOfInformationStatus.EXPIRED, label: 'Expired' };
    }
    if (releaseOfInformation.signed_at) {
      return { key: ReleaseOfInformationStatus.ACTIVE, label: 'Active' };
    }
    return { key: ReleaseOfInformationStatus.PENDING, label: 'Pending' };
  }, [
    releaseOfInformation.signed_at,
    releaseOfInformation.expired_at,
    releaseOfInformation.revoked_at
  ]);

  const recipientName = useMemo(() => {
    if (!releaseOfInformation.recipient) return 'Unknown Recipient';

    if (
      // ! 😬 Kinda hacky, this should imply ORGANIZATION
      'kustomer_id' in releaseOfInformation.recipient &&
      'name' in releaseOfInformation.recipient
    ) {
      return releaseOfInformation.recipient.name;
    }

    return formatPersonName({
      person: releaseOfInformation.recipient,
      withNickname: false
    });
  }, [releaseOfInformation.recipient]);

  const isPersonRecipient =
    !!releaseOfInformation.recipient && !('kustomer_id' in releaseOfInformation.recipient);

  const concerningName = useMemo(() => {
    if (!releaseOfInformation.concerning) return 'Unknown Person';

    return formatPersonName({ person: releaseOfInformation.concerning, withNickname: false });
  }, [releaseOfInformation.concerning]);

  const color = statusColorMap[status.key];
  const roiConsent = releaseOfInformation.consent || [];

  return (
    <ListCard
      {...(showHealthieLink && {
        headingActions: (
          <a
            className="text-gray-500 hover:text-gray-700 flex items-center"
            href={`${healthieDomain}/form_answer_groups/${releaseOfInformation.form_answer_group_healthie_id}`}
            rel="noreferrer"
            target="_blank"
            title="View in Healthie">
            <FontAwesomeIcon icon={faExternalLink} />
          </a>
        )
      })}
      debug={releaseOfInformation}
      headingIcon={statusIconMap[status.key]}
      headingIconClassName={`text-${color}-700`}
      subtitle={`Signed with: ${recipientName}`}
      subtitleSensitive={isPersonRecipient}
      title={`Release of Information - ${status.label}`}>
      <div className="flex flex-col gap-2 mt-2">
        {/* Status Badge */}
        <div className="flex gap-1">
          <Badge color={color} text={status.label} />
        </div>

        {/* Member Service Date Badge */}
        {releaseOfInformation.service_start_at && releaseOfInformation.service_end_at && (
          <div className="flex gap-4">
            {new Date(releaseOfInformation.service_end_at) <
            new Date(releaseOfInformation.service_start_at) ? (
              <Badge
                color={Color.RED}
                text={`Invalid service dates: End date (${new Date(releaseOfInformation.service_end_at).toLocaleDateString()}) is before start date (${new Date(releaseOfInformation.service_start_at).toLocaleDateString()})`}
                fontSize={'text-base'}
              />
            ) : (
              <Badge
                color={Color.BLUE}
                text={`Member defined service dates: ${new Date(releaseOfInformation.service_start_at).toLocaleDateString()} to ${new Date(releaseOfInformation.service_end_at).toLocaleDateString()}`}
                fontSize={'text-base'}
              />
            )}
          </div>
        )}

        {/* Key Information */}
        <div className="flex flex-col gap-2 text-sm">
          {releaseOfInformation.signed_at && (
            <div>
              <span className="font-medium text-gray-600">
                Signed Date:
                <Tooltip tooltipContent="The form value filled out by the member">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="ml-1 text-xs text-gray-400 hover:text-gray-600"
                  />
                </Tooltip>
              </span>
              <br />
              <span>{new Date(releaseOfInformation.signed_at).toLocaleDateString()}</span>
            </div>
          )}

          {releaseOfInformation.expired_at && (
            <div>
              <span className="font-medium text-gray-600">Expiration Date:</span>
              <br />
              <span>{new Date(releaseOfInformation.expired_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Consent Granted */}
        <div className="text-sm">
          <span className="font-medium text-gray-600">Consent Granted:</span>
          {roiConsent.length > 0 ? (
            <ul className="list-disc list-inside mt-1 ml-2 text-gray-700 space-y-0.5">
              {roiConsent.map((consentItem, index) => (
                <li key={`${consentItem}-${index}`} className="break-words leading-snug text-xs">
                  {consentItem}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 ml-2 text-gray-500 italic">No consent granted</p>
          )}
        </div>

        {/* Show/Hide Details Button */}
        <Button
          className="mt-2"
          color={Color.WHITE}
          onClick={() => setShowAllDetails(!showAllDetails)}
          size="xs">
          {showAllDetails ? 'Hide Details' : 'Show More Details'}
        </Button>

        {/* Full Details */}
        {showAllDetails && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm space-y-2">
            <div>
              <span className="font-medium text-gray-600">Release of Information ID:</span>
              <span className="ml-2 font-mono text-xs">{releaseOfInformation.id}</span>
            </div>

            {releaseOfInformation.revoked_at && (
              <div>
                <span className="font-medium text-gray-600">Revoked Date:</span>
                <span className="ml-2">
                  {new Date(releaseOfInformation.revoked_at).toLocaleString()}
                </span>
              </div>
            )}

            {releaseOfInformation.revoked_by && (
              <div>
                <span className="font-medium text-gray-600">Revoked By:</span>
                <span className="ml-2">
                  {formatStaffName({ staff: releaseOfInformation.revoked_by })}
                </span>
              </div>
            )}

            {releaseOfInformation.actor && (
              <div>
                <span className="font-medium text-gray-600">Requested By:</span>
                <span className="ml-2">
                  {formatStaffName({ staff: releaseOfInformation.actor })}
                </span>
              </div>
            )}

            {releaseOfInformation.concerning && (
              <div>
                <span className="font-medium text-gray-600">Concerning:</span>
                <span className="mp-sensitive ml-2">{concerningName}</span>
              </div>
            )}

            {releaseOfInformation.requested_form_completion_signed_at && (
              <div>
                <span className="font-medium text-gray-600">System Signed At Date:</span>
                <span className="ml-2">
                  {new Date(
                    releaseOfInformation.requested_form_completion_signed_at
                  ).toLocaleString()}
                </span>
              </div>
            )}

            <div>
              <span className="font-medium text-gray-600">Created:</span>
              <span className="ml-2">
                {new Date(releaseOfInformation.created_at).toLocaleString()}
              </span>
            </div>

            <div>
              <span className="font-medium text-gray-600">Last Updated:</span>
              <span className="ml-2">
                {new Date(releaseOfInformation.updated_at).toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </div>
    </ListCard>
  );
};

export default memo(ReleaseOfInformationItem);
